import { useState, useEffect } from 'react'
import { SkeletonStats, SkeletonTable, SkeletonCard } from '../components/SkeletonLoader';
import { FiUsers, FiFilter, FiDownload, FiPlus, FiX, FiAlertCircle, FiLock } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { empresasAPI } from '../services/api';
import { API_URL } from '../config/api';
import ModalResetPasswordAdmin from '../components/v2/ModalResetPasswordAdmin';
import { PageHeader, Btn } from '../ui';
import { formatearFecha } from '../utils/fechas';

const GestionUsuariosGlobal = () => {
  const [empleados, setEmpleados] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [creando, setCreando] = useState(false);
  const [modalResetPassword, setModalResetPassword] = useState(null); // empleado o null

  const [filtros, setFiltros] = useState({
    empresa: '',
    rol: '',
    activo: '',
    busqueda: ''
  });
  const [formData, setFormData] = useState({
    empresa_id: '',
    nombre: '',
    email: '',
    password: '',
    passwordConfirm: '',
    dni: '',
    telefono: '',
    rol: 'empleado',
    is_admin: false,
    fecha_inicio: '',
    fecha_fin: '',
    grupo_id: '',
    calendario_id: ''
  });
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [calendariosEmpresa, setCalendariosEmpresa] = useState([]);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [empleadosResp, empresasResp] = await Promise.all([
        empresasAPI.getEmpleadosGlobal(),
        empresasAPI.getAll()
      ]);
      setEmpleados(empleadosResp.data.empleados || []);
      setEmpresas(empresasResp.data.empresas || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const cargarGruposCalendarios = async (empresaId) => {
    if (!empresaId) { setGruposEmpresa([]); setCalendariosEmpresa([]); return; }
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      const [gRes, cRes] = await Promise.all([
        fetch(`${API_URL}/grupos?empresa_id=${empresaId}`, { headers }),
        fetch(`${API_URL}/calendarios?empresa_id=${empresaId}`, { headers })
      ]);
      if (gRes.ok) { const d = await gRes.json(); setGruposEmpresa(d.grupos || d || []); }
      if (cRes.ok) { const d = await cRes.json(); setCalendariosEmpresa(d.calendarios || d || []); }
    } catch { /* silencioso */ }
  };

  const abrirModalCrear = () => {
    setGruposEmpresa([]);
    setCalendariosEmpresa([]);
    setFormData({
      empresa_id: '', nombre: '', email: '', password: '', passwordConfirm: '',
      dni: '', telefono: '', rol: 'empleado', is_admin: false,
      fecha_inicio: '', fecha_fin: '', grupo_id: '', calendario_id: ''
    });
    setModalAbierto(true);
  };

  const handleCrearEmpleado = async (e) => {
    e.preventDefault();

    // Validaciones básicas
    if (!formData.nombre || !formData.email || !formData.password) {
      toast.error('Nombre, email y contraseña son obligatorios');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener mínimo 6 caracteres');
      return;
    }
    if (formData.password !== formData.passwordConfirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    // Solo super_admin no necesita empresa
    if (formData.rol !== 'super_admin' && !formData.empresa_id) {
      toast.error('Debes seleccionar una empresa para este rol');
      return;
    }
    if (formData.rol === 'empleado' && formData.empresa_id && !formData.calendario_id && !formData.grupo_id) {
      toast.error('Asigna un grupo con calendario o un calendario personal');
      return;
    }

    setCreando(true);
    try {
      const token = localStorage.getItem('token');

      // Para super_admin, usar endpoint diferente (sin empresa)
      const url = formData.rol === 'super_admin'
        ? `${API_URL}/empleados/super-admin`
        : `${API_URL}/empresas/${formData.empresa_id}/empleados`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nombre: formData.nombre,
          email: formData.email,
          password: formData.password,
          dni: formData.dni,
          telefono: formData.telefono,
          is_admin: formData.rol === 'admin' || formData.is_admin,
          rol: formData.rol,
          fecha_inicio: formData.fecha_inicio || null,
          fecha_fin: formData.fecha_fin || null,
          grupo_id: formData.grupo_id ? parseInt(formData.grupo_id) : null,
          calendario_id: formData.calendario_id ? parseInt(formData.calendario_id) : null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al crear empleado');
      }

      toast.success('Usuario creado correctamente');
      setModalAbierto(false);
      cargarDatos();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreando(false);
    }
  };

  const empresasUnicas = [...new Set(empleados.map(e => e.empresa_nombre))].sort();

  const empleadosFiltrados = empleados.filter(empleado => {
    // Filtro por empresa
    if (filtros.empresa && empleado.empresa_nombre !== filtros.empresa) {
      return false;
    }

    // Filtro por rol
    if (filtros.rol && empleado.rol !== filtros.rol) {
      return false;
    }

    // Filtro por estado
    if (filtros.activo !== '' && empleado.activo !== parseInt(filtros.activo)) {
      return false;
    }

    // Filtro por búsqueda
    if (filtros.busqueda) {
      const busqueda = filtros.busqueda.toLowerCase();
      return (
        empleado.nombre?.toLowerCase().includes(busqueda) ||
        empleado.email?.toLowerCase().includes(busqueda) ||
        empleado.dni?.toLowerCase().includes(busqueda) ||
        empleado.empresa_nombre?.toLowerCase().includes(busqueda)
      );
    }

    return true;
  });

  const getRolBadgeColor = (rol) => {
    const colors = {
      super_admin: 'bg-red-100 text-red-800',
      admin: 'bg-purple-100 text-purple-800',
      empleado: 'bg-blue-100 text-blue-800'
    };
    return colors[rol] || colors.empleado;
  };

  const getRolLabel = (rol) => {
    const labels = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      empleado: 'Empleado'
    };
    return labels[rol] || rol;
  };

  const exportarCSV = () => {
    try {
      const headers = ['Empresa', 'Nombre', 'Email', 'DNI', 'Teléfono', 'Rol', 'Estado', 'Fecha Registro'];
      const rows = empleadosFiltrados.map(emp => [
        emp.empresa_nombre || '',
        emp.nombre || '',
        emp.email || '',
        emp.dni || '',
        emp.telefono || '',
        getRolLabel(emp.rol),
        emp.activo ? 'Activo' : 'Inactivo',
        formatearFecha(emp.created_at)
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `empleados_global_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      toast.success('CSV exportado correctamente');
    } catch (error) {
      console.error('Error exportando CSV:', error);
      toast.error('Error al exportar CSV');
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      empresa: '',
      rol: '',
      activo: '',
      busqueda: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ U1"
        kicker="Usuarios"
        title="Usuarios"
        italic="globales."
        sub={`Gestión global de usuarios (${empleadosFiltrados.length} de ${empleados.length}).`}
        actions={
          <>
            <Btn variant="ghost" size="sm" onClick={exportarCSV}><FiDownload size={14} /> Exportar CSV</Btn>
            <Btn variant="primary" size="sm" onClick={abrirModalCrear}><FiPlus size={14} /> Nuevo usuario</Btn>
          </>
        }
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      {/* ══ CONTENIDO ══ */}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--ink-2)]">Total Empleados</p>
              <p className="text-2xl font-bold text-[var(--ink)]">{empleados.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiUsers className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--ink-2)]">Empleados Activos</p>
              <p className="text-2xl font-bold text-green-600">
                {empleados.filter(e => e.activo).length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <FiUsers className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--ink-2)]">Administradores</p>
              <p className="text-2xl font-bold text-purple-600">
                {empleados.filter(e => e.rol === 'admin' || e.rol === 'super_admin').length}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <FiUsers className="text-purple-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--ink-2)]">Empresas</p>
              <p className="text-2xl font-bold text-orange-600">
                {empresasUnicas.length}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <FiUsers className="text-orange-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-4">
          <FiFilter className="text-[var(--ink-2)]" size={20} />
          <h3 className="font-semibold text-[var(--ink)]">Filtros</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Búsqueda */}
          <div className="md:col-span-2">
            <input
              type="text"
              placeholder="Buscar por nombre, email, DNI..."
              value={filtros.busqueda}
              onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
              className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Empresa */}
          <div>
            <select
              value={filtros.empresa}
              onChange={(e) => setFiltros({ ...filtros, empresa: e.target.value })}
              className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas las empresas</option>
              {empresasUnicas.map(empresa => (
                <option key={empresa} value={empresa}>{empresa}</option>
              ))}
            </select>
          </div>

          {/* Rol */}
          <div>
            <select
              value={filtros.rol}
              onChange={(e) => setFiltros({ ...filtros, rol: e.target.value })}
              className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="empleado">Empleado</option>
            </select>
          </div>

          {/* Estado */}
          <div>
            <select
              value={filtros.activo}
              onChange={(e) => setFiltros({ ...filtros, activo: e.target.value })}
              className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los estados</option>
              <option value="1">Activos</option>
              <option value="0">Inactivos</option>
            </select>
          </div>
        </div>

        {(filtros.empresa || filtros.rol || filtros.activo || filtros.busqueda) && (
          <div className="mt-3">
            <button
              onClick={limpiarFiltros}
              className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Empleado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  DNI
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Teléfono
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Fecha Registro
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {empleadosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-[var(--ink-3)]">
                    {empleados.length === 0
                      ? 'No hay empleados registrados'
                      : 'No se encontraron empleados con los filtros aplicados'}
                  </td>
                </tr>
              ) : (
                empleadosFiltrados.map((empleado) => (
                  <tr key={`${empleado.empresa_id}-${empleado.id}`} className="hover:bg-[var(--bg-2)]">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-[var(--ink)]">
                        {empleado.empresa_nombre}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-[var(--ink)]">
                          {empleado.nombre}
                        </div>
                        <div className="text-sm text-[var(--ink-3)]">{empleado.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--ink-3)]">
                      {empleado.dni || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--ink-3)]">
                      {empleado.telefono || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRolBadgeColor(empleado.rol)}`}>
                        {getRolLabel(empleado.rol)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        empleado.activo
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {empleado.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--ink-3)]">
                      {formatearFecha(empleado.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => setModalResetPassword(empleado)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Resetear contraseña"
                      >
                        <FiLock size={13} />
                        Resetear contraseña
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal reset password (super admin) */}
      {modalResetPassword && (
        <ModalResetPasswordAdmin
          empleado={modalResetPassword}
          onClose={() => setModalResetPassword(null)}
        />
      )}

      {/* Modal de crear usuario */}
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setModalAbierto(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--line)]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FiPlus className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="text-base font-bold text-[var(--ink)]">Nuevo usuario</h3>
              </div>
              <button onClick={() => setModalAbierto(false)} className="w-8 h-8 flex items-center justify-center rounded-lg text-[var(--ink-4)] hover:text-[var(--ink-2)] hover:bg-[var(--bg-2)]">
                <FiX className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCrearEmpleado} className="px-6 py-5">
              <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                {/* Asignación */}
                <div className="col-span-3">
                  <p className="text-[11px] font-semibold text-[var(--ink-4)] uppercase tracking-wider mb-2">Asignación</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Rol *</label>
                  <select value={formData.rol}
                    onChange={(e) => { setFormData({ ...formData, rol: e.target.value, empresa_id: '', grupo_id: '', calendario_id: '' }); setGruposEmpresa([]); setCalendariosEmpresa([]); }}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" required>
                    <option value="empleado">Empleado</option>
                    <option value="admin">Admin de Empresa</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                {formData.rol !== 'super_admin' ? (
                  <div>
                    <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Empresa *</label>
                    <select value={formData.empresa_id}
                      onChange={(e) => { setFormData({ ...formData, empresa_id: e.target.value, grupo_id: '', calendario_id: '' }); cargarGruposCalendarios(e.target.value); }}
                      className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" required>
                      <option value="">Selecciona empresa</option>
                      {empresas.filter(emp => emp.activo).map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="col-span-2 flex items-end">
                    <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-xl w-full">
                      <FiAlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">Acceso global. Sin empresa asociada.</p>
                    </div>
                  </div>
                )}
                {formData.rol !== 'super_admin' && (
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer hover:bg-purple-50 transition-colors w-full">
                      <input type="checkbox" checked={formData.is_admin} onChange={e => setFormData({...formData, is_admin: e.target.checked})}
                        className="w-4 h-4 text-purple-600 rounded" />
                      <span className="text-sm font-medium text-[var(--ink-2)]">Administrador</span>
                    </label>
                  </div>
                )}

                {/* Datos personales */}
                <div className="col-span-3 mt-2">
                  <p className="text-[11px] font-semibold text-[var(--ink-4)] uppercase tracking-wider mb-2">Datos personales</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Nombre completo *</label>
                  <input type="text" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="Nombre y apellidos" required />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Email *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="usuario@empresa.com" required />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">DNI/NIF</label>
                  <input type="text" value={formData.dni} onChange={(e) => setFormData({ ...formData, dni: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="12345678A" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Teléfono</label>
                  <input type="tel" value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="+34 600..." />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Fecha inicio</label>
                  <input type="date" value={formData.fecha_inicio} onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Fecha fin</label>
                  <input type="date" value={formData.fecha_fin} onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                </div>

                {/* Acceso */}
                <div className="col-span-3 mt-2">
                  <p className="text-[11px] font-semibold text-[var(--ink-4)] uppercase tracking-wider mb-2">Acceso</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Contraseña *</label>
                  <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" placeholder="Mínimo 6 caracteres" required minLength={6} />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Confirmar contraseña *</label>
                  <input type="password" value={formData.passwordConfirm} onChange={(e) => setFormData({ ...formData, passwordConfirm: e.target.value })}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 bg-white ${formData.passwordConfirm && formData.password !== formData.passwordConfirm ? 'border-red-300 focus:ring-red-300' : 'border-[var(--line-2)] focus:ring-blue-500 focus:border-blue-500'}`}
                    placeholder="Repite la contraseña" required minLength={6} />
                  {formData.passwordConfirm && formData.password !== formData.passwordConfirm && (
                    <p className="text-xs text-red-500 mt-1">No coinciden</p>
                  )}
                </div>
                <div />

                {/* Organización — solo si hay empresa */}
                {formData.rol !== 'super_admin' && formData.empresa_id && (
                  <>
                    <div className="col-span-3 mt-2">
                      <p className="text-[11px] font-semibold text-[var(--ink-4)] uppercase tracking-wider mb-2">Organización</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Grupo</label>
                      <select value={formData.grupo_id} onChange={e => setFormData({...formData, grupo_id: e.target.value})} className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                        <option value="">— Sin grupo —</option>
                        {gruposEmpresa.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[var(--ink-3)] mb-1 block">Calendario personal</label>
                      <select value={formData.calendario_id} onChange={e => setFormData({...formData, calendario_id: e.target.value})} className="w-full px-3 py-2 text-sm border border-[var(--line-2)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                        <option value="">— Del grupo —</option>
                        {calendariosEmpresa.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                      </select>
                    </div>
                    <div />
                  </>
                )}
              </div>

              <div className="flex gap-3 pt-4 mt-4 border-t border-[var(--line)]">
                <button type="button" onClick={() => setModalAbierto(false)} disabled={creando} className="flex-1 btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" disabled={creando} className="flex-1 btn btn-primary btn-ripple">
                  {creando ? 'Creando...' : 'Crear usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default GestionUsuariosGlobal;
